/**
 * DI-3-C — the proposal row's chips: D91's recipe through `statusChipStyle`
 * (the hue owns the fill, the dot and the label; no keyline), compact. Board 1
 * draws the first light WITH a dot and the flight-log chip without one.
 *
 * DI-3-S20-FE — `quiet`: information, not a status to act on ("no matching
 * log among your logs yet" before Start). The slot keeps the fill; the label
 * reads in the body's secondary text colour at regular weight.
 */
import type { ReactElement } from "react";
import { Box, Chip, useTheme } from "@mui/material";
import {
  radius,
  spacing,
  statusChipStyle,
  type StatusSlot,
} from "@aeriosiq/ui";

export function SlotChip({
  slot,
  label,
  dot = false,
  quiet = false,
  testId,
}: {
  slot: StatusSlot;
  label: string;
  dot?: boolean;
  quiet?: boolean;
  testId?: string;
}): ReactElement {
  const theme = useTheme();
  const style = statusChipStyle(theme, slot);
  return (
    <Chip
      size="small"
      label={label}
      data-slot={slot}
      {...(quiet ? { "data-quiet": "true" } : {})}
      {...(testId ? { "data-testid": testId } : {})}
      {...(dot
        ? {
            icon: (
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: spacing.sm,
                  height: spacing.sm,
                  borderRadius: `${String(radius.pill)}px`,
                  backgroundColor: style.dotColor,
                  flexShrink: 0,
                }}
              />
            ),
          }
        : {})}
      sx={{
        backgroundColor: style.backgroundColor,
        color: quiet ? "text.secondary" : style.color,
        ...(quiet ? { fontWeight: "fontWeightRegular" } : {}),
        flexShrink: 0,
        maxWidth: "100%",
        "& .MuiChip-icon": { ml: 1, mr: -0.25 },
      }}
    />
  );
}
